import { Sun, Wind, Droplets, ThermometerSun } from 'lucide-react';
import Section from './Section.jsx';
import ForecastStrip from './ForecastStrip.jsx';
import { formatTime } from '../lib/format.js';

// NWS daily winds are mph; "kt" belongs only in the Marine accordion. Old
// archived briefs carry the pre-normalization shape ({speed, direction}) whose
// speed was always mph too, so the fallback re-units them correctly.
function windLabel(wind) {
  if (!wind) return null;
  if (wind.label) return wind.label;
  if (wind.speed) return `${wind.direction} ${wind.speed} mph`.trim();
  return null;
}

export default function WeatherOverview({ summary, details, forecast }) {
  // Guard against a reversed pair (old archived briefs generated while NWS's
  // first period was still "Overnight" have high and low swapped).
  const canSwap = details?.tempHigh != null && details?.tempLow != null;
  const high = canSwap ? Math.max(details.tempHigh, details.tempLow) : details?.tempHigh;
  const low = canSwap ? Math.min(details.tempHigh, details.tempLow) : details?.tempLow;
  const wind = details ? windLabel(details.wind) : null;
  return (
    <Section id="weather" title="Weather" eyebrow="Today">
      <p className="mb-4 leading-relaxed">{summary}</p>
      {details && (
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <Stat icon={ThermometerSun} label="High / Low" value={`${high}° / ${low}°`} />
          {wind && <Stat icon={Wind} label="Wind" value={wind} />}
          <Stat icon={Droplets} label="Rain" value={`${details.rainChance}%`} />
          {/* A slash, like "High / Low": the owner rule bans dashes in copy, and
              " to " wraps the 390px stat cell (measured with the real font). */}
          <Stat icon={Sun} label="Sun" value={`${formatTime(details.sunrise)} / ${formatTime(details.sunset)}`} />
        </dl>
      )}
      <ForecastStrip forecast={forecast} />
    </Section>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-cream-100 px-3 py-2.5">
      <div className="mb-1 flex items-center gap-1.5 text-xs uppercase tracking-wide text-navy-500">
        <Icon className="h-3.5 w-3.5" />
        <span>{label}</span>
      </div>
      <div className="font-display text-base font-semibold text-navy-900">{value}</div>
    </div>
  );
}
